import { ROLE_INFO, TEAM_NAMES, type GameNotice, type Phase, type PlayerView, type RoleId } from '@mafia/core';

export const PHASE_NAMES: Record<Phase, string> = {
  ROLE_REVEAL: 'Раздача ролей',
  NIGHT: 'Ночь',
  MORNING: 'Утро',
  DAY_SPEECH: 'День · речи',
  DISCUSSION: 'Общее обсуждение',
  VOTING: 'Голосование',
  TIE_SPEECH: 'Оправдательные речи',
  LAST_WORD: 'Последнее слово',
  GAME_OVER: 'Игра окончена',
};

const ERRORS: Record<string, string> = {
  UNAUTHORIZED: 'Требуется вход',
  BANNED: 'Аккаунт заблокирован',
  ROOM_NOT_FOUND: 'Комната не найдена или уже закрыта',
  ROOM_FULL: 'В комнате нет мест',
  WRONG_PASSWORD: 'Неверный пароль комнаты',
  ALREADY_IN_GAME: 'Вы уже участвуете в другой игре',
  NOT_HOST: 'Это может сделать только хост',
  NOT_ENOUGH_PLAYERS: 'Недостаточно игроков',
  PLAYERS_NOT_READY: 'Не все игроки готовы',
  INVALID_SETTINGS: 'Некорректные настройки',
  GAME_IN_PROGRESS: 'В комнате уже идёт игра',
  KICKED: 'Хост исключил вас из этой комнаты',
  ROOM_CLOSED: 'Комната закрыта',
  VALIDATION: 'Некорректные данные',
  RATE_LIMITED: 'Слишком часто, подождите секунду',
  WRONG_PHASE: 'Сейчас это действие недоступно',
  NOT_A_PLAYER: 'Вы не участник этой игры',
  PLAYER_DEAD: 'Вы выбыли из игры',
  NOT_YOUR_TURN: 'Сейчас не ваша очередь',
  ACTION_NOT_ALLOWED: 'Вашей роли это действие недоступно',
  INVALID_TARGET: 'Этого игрока выбрать нельзя',
  ALREADY_DONE: 'Вы уже сделали это',
  SILENCED: 'Сегодня вы молчите и не голосуете — ночь с любовницей',
  NOT_MODERATOR: 'Это может делать только ведущий',
  TOURNAMENT_ADMIN_ONLY: 'Турнирную игру запускает только администратор',
  STALE_TIMER: 'Время вышло',
  STALE_PHASE: 'Фаза уже сменилась — нажатие пропущено',
  MODERATOR_LEADS: 'Слово передаёт ведущий',
  TIMEOUT: 'Сервер не ответил',
  INTERNAL: 'Внутренняя ошибка сервера',
};

export const errorText = (code: string, message?: string) => message || ERRORS[code] || 'Что-то пошло не так';

export const roleName = (role: RoleId) => ROLE_INFO[role].name;
export const teamName = TEAM_NAMES;

export function seatLabel(view: Pick<PlayerView, 'players'> | null, seat: number) {
  const p = view?.players.find((x) => x.seat === seat);
  return p ? `№${seat} ${p.nickname}` : `№${seat}`;
}

export function plural(n: number, one: string, few: string, many: string) {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return one;
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return few;
  return many;
}

export const formatDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString('ru-RU', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';

/** Текст тоста для уведомления. null — без тоста (информация уже видна на экране). */
export function noticeText(n: GameNotice, view: PlayerView | null): { text: string; tone: 'info' | 'good' | 'bad' } | null {
  switch (n.type) {
    // Итоги ночи показывает анимация утра — тост бы её дублировал.
    case 'NIGHT_RESULT':
      return null;
    case 'CHECK_RESULT':
      // Ведущий получает проверки обеих ролей — подписываем, чья это проверка.
      if (view?.isModerator) {
        const who = n.action === 'SHERIFF_CHECK' ? 'Шериф' : 'Дон';
        const res = n.action === 'SHERIFF_CHECK' ? (n.positive ? 'мафия' : 'не мафия') : n.positive ? 'шериф' : 'не шериф';
        return { text: `${who} проверил ${seatLabel(view, n.target)}: ${res}`, tone: 'info' };
      }
      if (n.action === 'SHERIFF_CHECK') {
        return n.positive
          ? { text: `${seatLabel(view, n.target)} — МАФИЯ`, tone: 'bad' }
          : { text: `${seatLabel(view, n.target)} — не мафия`, tone: 'good' };
      }
      return n.positive
        ? { text: `${seatLabel(view, n.target)} — ШЕРИФ`, tone: 'bad' }
        : { text: `${seatLabel(view, n.target)} — не шериф`, tone: 'info' };
    case 'VOTE_RESULT':
      if (n.eliminated) return { text: `Голосованием исключён ${seatLabel(view, n.eliminated)}`, tone: 'bad' };
      if (n.skipped) return { text: 'Стол решил пропустить голосование', tone: 'info' };
      return { text: n.tie ? 'Голоса разделились поровну' : 'Никто не исключён', tone: 'info' };
    case 'PLAYER_LEFT':
      return { text: `${seatLabel(view, n.seat)} покинул игру`, tone: 'info' };
    case 'GAME_OVER':
      return { text: n.winner === 'DRAW' ? 'Ничья!' : `Победа: ${TEAM_NAMES[n.winner]}`, tone: 'good' };
    case 'PHASE_CHANGED':
      return null;
  }
}
